import { api, API_BASE } from './client'

export interface ClubResponse {
  clubs: Array<{
    club_id: number
    name: string
    logo_url: string | null
    primary_color: string | null
    secondary_color: string | null
    accent_color: string | null
    club_role: string
    player_count: number
    seat_limit: number | null
    /** Fecha (YYYY-MM-DD) hasta la que el servicio VR está habilitado. Null = sin vencimiento. */
    service_until: string | null
    /** Derivado: `service_until == null || service_until >= hoy`. */
    service_active: boolean
    players: Array<{
      user_id: number
      display_name: string
      email: string | null
      train_enabled: boolean
      date_joined: string | null
      last_login: string | null
    }>
  }>
}

export interface ClubOverview {
  club: {
    club_id: number
    name: string
    logo_url: string | null
    primary_color: string | null
    secondary_color: string | null
    accent_color: string | null
    club_role: string
    seat_limit: number | null
    division: string | null
    created_at: string
    /** Fecha (YYYY-MM-DD) hasta la que el servicio VR está habilitado. Null = sin vencimiento. */
    service_until: string | null
    /** Derivado: `service_until == null || service_until >= hoy`. */
    service_active: boolean
    player_count: number
  }
  players: Array<{
    user_id: number
    display_name: string
    email: string | null
    is_active: boolean
    train_enabled: boolean
    category: string | null
    date_joined: string | null
    last_login: string | null
    n_sessions: number
    last_session_at: string | null
    questionnaires: Array<{
      code: string
      timepoint: string
      submitted_at: string
    }>
    pending_invitations: Array<{
      code: string
      timepoint: string | null
      expires_at: string
      /** Link único del cuestionario — el staff lo reenvía a mano (WhatsApp). */
      link_url: string
    }>
    /**
     * Semáforo de onboarding (derivado, no columnas de DB propias — ver
     * GET /club/{id}/overview en el backend). `consent_ok`: existe Consent
     * vigente (revoked_at null). `profile_ok`: sin campos demográficos
     * requeridos faltantes. `questionnaire_ok`: sin invitaciones a
     * cuestionario pendientes para su email. `sessions_count`: sesiones
     * status=closed (distinto de `n_sessions`, que cuenta todos los status).
     */
    consent_ok: boolean
    profile_ok: boolean
    questionnaire_ok: boolean
    sessions_count: number
    /** El jugador ya fijó su PIN de 6 dígitos (login del kit). */
    has_pin: boolean
    /**
     * Vencimiento del link `/set-pin` vivo (sin usar y sin vencer), o null si
     * no hay ninguno. El Plantel lo usa como pista de "el link que copiaste
     * sigue sirviendo"; copiar de nuevo reusa ese mismo token (ver
     * `club.playerPinLink`).
     */
    pin_token_expires_at: string | null
  }>
}

export interface ClubPlayerTimeline {
  athlete: {
    user_id: number
    display_name: string | null
    email: string | null
  }
  /**
   * Semáforo de onboarding de este jugador puntual — mismos flags que
   * `ClubOverview.players[].{consent_ok,profile_ok,questionnaire_ok,
   * train_enabled,sessions_count}` (ver GET
   * /club/{id}/players/{userId}/timeline en el backend).
   */
  onboarding: {
    consent_ok: boolean
    profile_ok: boolean
    questionnaire_ok: boolean
    train_enabled: boolean
    sessions_count: number
  }
  events: Array<
    | {
        type: 'questionnaire'
        date: string | null
        response_id: number
        code: string
        timepoint: string
        scores: {
          subscales: Record<string, { raw: number; min: number; max: number; label: string }>
          total: { raw: number; min: number; max: number; label: string } | null
        }
      }
    | {
        type: 'session'
        date: string | null
        session_id: string
        status: string
        n_trials: number
        goals: number
        saves: number
        misses: number
        score_sum: number
        goal_rate: number | null
      }
  >
}

export interface ClubQuestionnaire {
  code: string
  name_es: string
}

export interface ClubPlayerInvitation {
  id: number
  questionnaire_code: string
  timepoint: string
  /** `scheduled` = re-test auto-agendado (T3/T4) que todavía no abrió: no
   * traba nada ni cuenta como pendiente hasta su fecha. */
  status: 'pending' | 'scheduled' | 'sent' | 'answered' | 'expired'
  created_at: string
  sent_at: string | null
  answered_at: string | null
}

/** Shape de `POST /club/{id}/players/{uid}/pin-link`. */
export interface ClubPlayerPinLink {
  user_id: number
  club_id: number
  /** URL absoluta `/set-pin?token=…` para mandar a mano (WhatsApp). */
  link: string
  expires_at: string
  has_pin: boolean
  /** true = se devolvió el token que ya estaba vivo; false = se emitió uno nuevo. */
  reused: boolean
}

/** Shape de `POST /club/me/terms` (ver backend `_staff_terms_dict`). */
export interface StaffTermsDto {
  id: number
  user_id: number
  version: string
  accepted_at: string
  created_at: string
}

export const club = {
  me: () => api.get<ClubResponse>('/club/me'),

  /** Self-terms de STAFF (gate BLOQUEANTE de /club, ver StaffTermsGate). Body
   *  vacío — idempotente: si ya aceptó, el backend devuelve la fila
   *  existente en vez de duplicar. */
  recordMyTerms: () => api.post<StaffTermsDto>('/club/me/terms', {}),

  overview: (clubId: number) =>
    api.get<ClubOverview>(`/club/${clubId}/overview`),

  addPlayer: (
    clubId: number,
    payload: {
      email: string
      display_name?: string
      category?: string
    },
  ) =>
    api.post<{
      user_id: number
      email: string
      display_name: string
      category: string | null
      reused: boolean
    }>(`/club/${clubId}/players`, payload),

  updatePlayer: (clubId: number, userId: number, patch: { category?: string }) =>
    api.patch<{ user_id: number; club_id: number; category: string | null }>(
      `/club/${clubId}/players/${userId}`,
      patch,
    ),

  invite: (
    clubId: number,
    payload: {
      emails: string[]
      code: string
      timepoint?: string | null
    },
  ) =>
    api.post<{
      created: number
      reused: number
      failed: Array<{ email: string; reason: string }>
    }>(`/club/${clubId}/invitations`, payload),

  exportUrl: (clubId: number, code?: string): string => {
    const q = new URLSearchParams()
    if (code) q.set('code', code)
    const qs = q.toString()
    return `${API_BASE}/club/${clubId}/export.csv${qs ? `?${qs}` : ''}`
  },

  playerTimeline: (clubId: number, userId: number) =>
    api.get<ClubPlayerTimeline>(`/club/${clubId}/players/${userId}/timeline`),

  questionnaires: (clubId: number) =>
    api.get<{ items: ClubQuestionnaire[] }>(`/club/${clubId}/questionnaires`),

  setPlayerTrain: (clubId: number, userId: number, enabled: boolean) =>
    api.patch<{ user_id: number; club_id: number; train_enabled: boolean }>(
      `/club/${clubId}/players/${userId}/train`,
      { enabled },
    ),

  /** Link de setup de PIN del jugador, para copiarlo y mandarlo a mano.
   *  Reusa el token vigente si lo hay; si venció, emite uno nuevo. Requiere
   *  staff del club (manager). */
  playerPinLink: (clubId: number, userId: number) =>
    api.post<ClubPlayerPinLink>(`/club/${clubId}/players/${userId}/pin-link`, {}),

  playerInvitations: (clubId: number, userId: number) =>
    api.get<{ items: ClubPlayerInvitation[] }>(
      `/club/${clubId}/players/${userId}/invitations`,
    ),
}
